import React, { useState } from 'react';
import Switch from '@primitives/Switch';
import Typography from '@primitives/Typography';
import { useDispatch, useSelector } from 'react-redux';
import {
  CosmosSyncBlockStatus,
  setBreakTimeStarted,
  setIsAgentOnline
} from '@cp/reducers/commonSlice';
import ModalDialog from '@primitives/ModalDialog';
import Chip from '@primitives/Chip';
import { RootState } from '@cp/src/store';
import sendAgentAvailabilityStatusToSlash from '@cp/utils/sendAgentAvailabilityStatusToSlash';
import { CALL_BRIDGE_STATUSES } from '@cp/pages/CaseDetails/feedbackForm/interfaces/index.type';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/components/TooltipV2/TooltipV2';
import cx from 'classnames';
import setAgentAvailability from '@cp/src/components/sidebar/actions/setAgentAvailability';
import { reasons } from '@cp/src/components/sidebar/constants/constants';
import { ExtensionHandler } from '@cp/utils/extension.utils';
import { toast } from '@primitives/Toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalStorage } from '@cp/utils/StorageUtils';
import styles from './index.module.scss';

function OnlineOfflineSwitch() {
  const dispatch = useDispatch();
  const isAgentOnline = useSelector((store: RootState) => store.common.isAgentOnline);
  const [isShowModalDialog, setIsShowModalDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const slashCallStatus = useSelector((store: RootState) => store.common.slashCallStatus);
  const ameyoCallData = useSelector((store: RootState) => store.ameyoSlice.ameyoCallData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isImpersonation = useSelector(
    (state: RootState) => state?.common?.userData?.userImpersonated
  );
  const isLonghornBlocked =
    useSelector((store: RootState) => store.common.cosmosSyncBlockStatus) ===
      CosmosSyncBlockStatus.BLOCKED && !isImpersonation;

  const isDisabled =
    (Boolean(ameyoCallData.ringingAt || ameyoCallData.acceptedAt) &&
      !ameyoCallData.disconnectedAt &&
      slashCallStatus === CALL_BRIDGE_STATUSES.INITIATED) ||
    isLonghornBlocked;

  const handleSwitchClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    //@ts-expect-error - checked is not available on target
    if (e.target?.checked) {
      setIsSubmitting(true);
      setAgentAvailability(true, selectedReason)
        .then(res => {
          const lastRouteBeforeOffline = LocalStorage.getItem('lastRouteBeforeOffline');
          if (lastRouteBeforeOffline) {
            navigate(lastRouteBeforeOffline);
            localStorage.removeItem('lastRouteBeforeOffline');
          }
          dispatch(setIsAgentOnline(true));
          sendAgentAvailabilityStatusToSlash(true);
        })
        .catch(err => {
          toast('Unable to set online please try again');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      setIsShowModalDialog(true);
    }
  };

  const handleFormSubmit = () => {
    setIsSubmitting(true);
    setAgentAvailability(false, selectedReason)
      .then(res => {
        const breakTimeStarted = res?.data?.breakStartTime;
        LocalStorage.setItem('lastRouteBeforeOffline', location.pathname);
        navigate('/');
        dispatch(setIsAgentOnline(false));
        setIsShowModalDialog(false);
        sendAgentAvailabilityStatusToSlash(false);
        ExtensionHandler.setAvailability(false); //setting ameyo offline
        dispatch(setBreakTimeStarted(breakTimeStarted));
      })
      .catch(err => {
        setIsShowModalDialog(false);
        toast.error('Unable to set offline please try again');
      })
      .finally(() => {
        setIsSubmitting(false);
        setSelectedReason('');
      });
  };

  const getTooltip = () => {
    if (isLonghornBlocked) {
      return 'All functions are disabled';
    }
    if (isDisabled) {
      return 'Please finish ongoing call first';
    }
    return `Turn ${isAgentOnline ? 'off' : 'on'} this toggle to mark yourself as ${
      isAgentOnline ? 'offline' : 'online'
    }`;
  };

  return (
    <>
      <Tooltip placement={'right'} hideStrategy="referenceHidden">
        <TooltipTrigger className="tooltipTriggerWrapper">
          <Switch
            onClick={handleSwitchClick}
            variant={'secondary'}
            checked={isAgentOnline}
            disabled={isDisabled || isSubmitting}
          />
        </TooltipTrigger>

        <TooltipContent
          className={cx(
            'tooltipWrapper',
            'z-[var(--z-index-ameyo-tooltip)]',
            '!bg-[var(--orange-oldlase)]',
            'text-[var(--grayscale-content-1)]',
            'leading-5',
            'shadow-lg',
            'p-2',
            'w-[156px]'
          )}
          arrowColor="var(--orange-oldlase)"
        >
          {getTooltip()}
        </TooltipContent>
      </Tooltip>

      <Typography variant="h3" className={cx([styles.text, styles.showText])}>
        <span>Online</span>
      </Typography>
      <ModalDialog
        open={isShowModalDialog}
        onClose={() => {
          setIsShowModalDialog(false);
          setSelectedReason('');
        }}
        header="Reason to mark yourself as offline"
        footerButtons={[
          {
            label: 'Cancel',
            onClick: () => {
              setIsShowModalDialog(false);
              setSelectedReason('');
            }
          },
          {
            label: 'Submit',
            onClick: () => {
              handleFormSubmit();
            },
            disabled: !selectedReason || isSubmitting
          }
        ]}
      >
        <div>
          <Typography variant={'p3'} color={'var(--grayscale-content-2)'}>
            All functions will be disabled while you are on break/offline
          </Typography>
          <div className="w-full">
            {reasons.map(reason => (
              <Chip
                label={reason.label}
                onChipClick={() => setSelectedReason(reason.label)}
                key={reason.label}
                wrapperClasses="mt-3 mr-3"
                selected={reason.label === selectedReason}
              />
            ))}
          </div>
        </div>
      </ModalDialog>
    </>
  );
}

export default OnlineOfflineSwitch;
